<script setup>

import notification from "@/utils/plugins/NotificationService";
import { computed, ref } from 'vue';

// Services
import MaterialService from '@/api/services/material.service';

// eslint-disable-next-line no-undef
const props = defineProps({
    material: Object,
})

// eslint-disable-next-line no-undef
const emit = defineEmits(['closeDialog'])

const emptyRow = () => ({ serialTag: '', internalCode: '', name: '' });

const rows = ref([emptyRow()]);
const saving = ref(false);

const sourceMaterial = computed(() => props.material || {});

const statusLabels = {
    available: 'Disponible',
    assigned: 'Asignado',
    decommissioned: 'Retirado',
};

const ownerTypeLabels = {
    systems: 'Sistemas',
    employee: 'Empleado',
};

const acquisitionTypeLabels = {
    purchased: 'Compra',
    renting: 'Renting',
};

const getStatusLabel = (status) => statusLabels[status] || status || '—';
const getOwnerTypeLabel = (ownerType) => ownerTypeLabels[ownerType] || ownerType || '—';
const getAcquisitionTypeLabel = (acquisitionType) => acquisitionTypeLabels[acquisitionType] || acquisitionType || '—';

// Mismas etiquetas que MaterialDialog.vue usa por tipo de material (Portátil, Monitor,
// Docking Station, Switch), para que la ficha de duplicación se lea igual que la de detalle.
const technicalSpecLabels = {
    antivirus: 'Antivirus',
    processor: 'Procesador',
    memory: 'Memoria',
    storage: 'Almacenamiento',
    os: 'Sistema Operativo',
    mac: 'MAC',
    resolution: 'Resolución',
    size: 'Tamaño',
    connection: 'Conexión',
    ports: 'Puertos',
    speed: 'Velocidad',
    firmware: 'Firmware',
};

const getTechnicalSpecLabel = (key) => technicalSpecLabels[key] || key;

const technicalSpecsEntries = computed(() => {
    const specs = sourceMaterial.value.technicalSpecs;
    if (!specs || typeof specs !== 'object') {
        return [];
    }
    return Object.entries(specs).filter(([, value]) => value !== null && value !== undefined && value !== '');
})

const addRow = () => {
    rows.value.push(emptyRow());
}

const removeRow = (index) => {
    if (rows.value.length === 1) {
        return;
    }
    rows.value.splice(index, 1);
}

const closeDialog = (changed = false) => {
    emit('closeDialog', changed);
}

const rowsAreValid = computed(() => {
    return rows.value.every(row => row.serialTag.trim() !== '' && row.internalCode.trim() !== '');
})

const duplicateMaterial = () => {
    if (!rowsAreValid.value) {
        notification.addNotification({
            type: 'error',
            message: 'Debes indicar número de serie y código interno en cada copia',
            duration: 6000
        })
        return;
    }

    const items = rows.value.map(row => {
        const item = {
            serialTag: row.serialTag.trim(),
            internalCode: row.internalCode.trim(),
        };
        if (row.name && row.name.trim() !== '') {
            item.name = row.name.trim();
        }
        return item;
    });

    saving.value = true;
    MaterialService.duplicateMaterial(sourceMaterial.value.id, { items })
        .then((res) => {
            if (res && (res.status === 200 || res.status === 201)) {
                notification.addNotification({
                    type: 'success',
                    message: items.length === 1
                        ? 'Se ha creado 1 copia del material correctamente'
                        : `Se han creado ${items.length} copias del material correctamente`,
                    duration: 6000
                })
                closeDialog(true);
            }
        })
        .finally(() => {
            saving.value = false;
        })
}

</script>

<template>
    <div>
        <div class="header">
            <img style="width: 30px; margin-bottom: 14px;" src="@/assets/icons/box-blue.svg">
            <span class="header-title pl-4">
                Duplicar Material
            </span>
        </div>
        <p class="duplicate-message">
            Se crearán nuevos materiales a partir del siguiente material, copiando marca, modelo, tipo y demás
            características. Indica el número de serie y el código interno de cada copia (obligatorios y únicos).
            El nombre es opcional; si se deja vacío se usará el del material original.
        </p>
        <div class="source-material-card">
            <div class="source-material-card__title">
                {{ sourceMaterial.name }}
            </div>
            <div class="source-material-card__grid">
                <div class="source-material-card__field">
                    <span class="source-material-card__label">Número de serie</span>
                    <span class="source-material-card__value">{{ sourceMaterial.serialTag || '—' }}</span>
                </div>
                <div class="source-material-card__field">
                    <span class="source-material-card__label">Código interno</span>
                    <span class="source-material-card__value">{{ sourceMaterial.internalCode || '—' }}</span>
                </div>
                <div class="source-material-card__field">
                    <span class="source-material-card__label">Marca</span>
                    <span class="source-material-card__value">{{ sourceMaterial.brand || '—' }}</span>
                </div>
                <div class="source-material-card__field">
                    <span class="source-material-card__label">Modelo</span>
                    <span class="source-material-card__value">{{ sourceMaterial.model || '—' }}</span>
                </div>
                <div class="source-material-card__field">
                    <span class="source-material-card__label">Tipo</span>
                    <span class="source-material-card__value">{{ sourceMaterial.type ? sourceMaterial.type.name : '—'
                        }}</span>
                </div>
                <div class="source-material-card__field">
                    <span class="source-material-card__label">Estado</span>
                    <span class="source-material-card__value">{{ getStatusLabel(sourceMaterial.status) }}</span>
                </div>
                <div class="source-material-card__field">
                    <span class="source-material-card__label">Tipo de dueño</span>
                    <span class="source-material-card__value">{{ getOwnerTypeLabel(sourceMaterial.ownerType) }}</span>
                </div>
                <div class="source-material-card__field">
                    <span class="source-material-card__label">Tipo de adquisición</span>
                    <span class="source-material-card__value">{{ getAcquisitionTypeLabel(sourceMaterial.acquisitionType)
                        }}</span>
                </div>
            </div>
            <div class="source-material-card__specs" v-if="technicalSpecsEntries.length > 0">
                <span class="source-material-card__label">Especificaciones técnicas</span>
                <div class="source-material-card__specs-grid">
                    <div class="source-material-card__spec" v-for="[key, value] in technicalSpecsEntries" :key="key">
                        <span class="source-material-card__spec-key">{{ getTechnicalSpecLabel(key) }}:</span> {{ value
                        }}
                    </div>
                </div>
            </div>
        </div>
        <div class="duplicate-rows">
            <div class="duplicate-row" v-for="(row, index) in rows" :key="index">
                <div class="duplicate-row__field">
                    <label class="w-100">Número de serie</label>
                    <el-input v-model="row.serialTag" placeholder="Número de serie" />
                </div>
                <div class="duplicate-row__field">
                    <label class="w-100">Código interno</label>
                    <el-input v-model="row.internalCode" placeholder="Código interno" />
                </div>
                <div class="duplicate-row__field">
                    <label class="w-100">Nombre (opcional)</label>
                    <el-input v-model="row.name" :placeholder="sourceMaterial.name" />
                </div>
                <div v-if="rows.length > 1" class="duplicate-row__remove">
                    <font-awesome-layers class="fa-2x pointer" title="Eliminar copia" @click="removeRow(index)">
                        <font-awesome-icon icon="circle" style="color:red" />
                        <font-awesome-icon icon="times" class="fa-inverse" transform="shrink-6" />
                    </font-awesome-layers>
                </div>
            </div>
        </div>
        <div class="mt-3 duplicate-add-copy">
            <button class="btn btn_yellow duplicate-add-copy__button" type="button" @click="addRow">
                + Añadir copia
            </button>
        </div>
        <!--  Botones guardar y cancelar -->
        <div class="row footer">
            <div class="col-md-12 mt-4" style="margin-left: -10px;">
                <div class="d-flex justify-content-end gap-3">
                    <div class="col-md-3">
                        <button class="btn btn-block btn_yellow edit-button" @click="closeDialog">Cancelar
                        </button>
                    </div>
                    <div class="col-md-3">
                        <button type="submit" class="btn btn-block btn_red edit-button" :disabled="saving"
                            @click="duplicateMaterial">
                            Guardar
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<style lang="scss" scoped>
.header-title {
    font-size: 30px;
    margin-left: 5px;
    font-weight: 800;
    letter-spacing: -0.05em;
    color: #474ce6;
}

.duplicate-message {
    margin: 12px 0 0;
    color: #1f2937;
    font-size: 14px;
    line-height: 1.4;
}

.source-material-card {
    margin-top: 14px;
    padding: 14px 18px;
    border-radius: 12px;
    background-color: #f5f6fb;
    border: 1px solid #e3e5f2;
}

.source-material-card__title {
    font-size: 18px;
    font-weight: 700;
    color: $font-blue-color;
    margin-bottom: 8px;
}

.source-material-card__grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
    gap: 10px 16px;
}

.source-material-card__field {
    display: flex;
    flex-direction: column;
}

.source-material-card__label {
    font-size: 12px;
    font-weight: 700;
    text-transform: uppercase;
    color: #6b7280;
    letter-spacing: 0.02em;
}

.source-material-card__value {
    font-size: 14px;
    color: #1f2937;
    font-weight: 600;
}

.source-material-card__specs {
    margin-top: 14px;
    padding-top: 10px;
    border-top: 1px solid #e3e5f2;
}

.source-material-card__specs-grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(180px, 1fr));
    gap: 4px 16px;
    margin-top: 6px;
}

.source-material-card__spec {
    font-size: 13px;
    color: #1f2937;
}

.source-material-card__spec-key {
    font-weight: 700;
    text-transform: capitalize;
}

.duplicate-rows {
    max-height: 45vh;
    overflow-y: auto;
    // Las filas Bootstrap usan márgenes laterales negativos. Sin limitar este eje,
    // el contenedor con scroll vertical muestra una barra horizontal innecesaria.
    overflow-x: hidden;
}

.duplicate-row {
    display: grid;
    grid-template-columns: repeat(3, minmax(0, 1fr)) auto;
    align-items: flex-end;
    gap: 20px;
    margin-top: 16px;
}

.duplicate-row__field {
    min-width: 0;
    text-align: center;
}

.duplicate-row__field :deep(.el-input__inner) {
    text-align: center;
}

.duplicate-row__remove {
    display: flex;
    justify-content: center;
    padding-bottom: 6px;
}

.duplicate-add-copy {
    padding: 0 2px;
}

.duplicate-add-copy__button {
    box-sizing: border-box;
    max-width: 100%;
    width: 100%;
}

@media (max-width: 767.98px) {
    .duplicate-row {
        grid-template-columns: 1fr;
        gap: 12px;
    }

    .duplicate-row__remove {
        justify-content: flex-end;
        padding-bottom: 0;
    }

}
</style>
